<template>
  <div class="departments-search-and-navigate">
    <div v-if="treeData.length === 0 && !loadingMore" class="empty-tree-state">
      <p>No departments found</p>
    </div>
    <div v-if="treeData.length === 0 && loadingMore" class="tree-skeleton-container">
      <div v-for="n in 5" :key="'skeleton-loading-' + n" class="tree-skeleton-item">
        <div class="tree-skeleton-icon"></div>
        <div class="tree-skeleton-content">
          <div class="tree-skeleton-title"></div>
        </div>
      </div>
    </div>
    <template v-else-if="treeData.length > 0">
      <TreeItem
        v-for="(item, index) in treeData"
        :key="`${item.type}-${item.id}`"
        :item="item"
        :expanded-items="expandedItems"
        :selected-item="selectedItem"
        :is-last-sibling="index === treeData.length - 1"
        :has-next-same-type="hasNextSameType(treeData, index)"
        :on-position-dropped-on-department="onPositionDroppedOnDepartment"
        :position-drag-source="positionDragSource"
        @toggle="handleToggle"
        @item-selected="handleItemSelected"
        @position-department-drag-over="handlePositionDepartmentDragOver"
      />
      <div
        v-if="treeData.length > 0 && loadingMore && hasMore"
        class="tree-skeleton-container tree-skeleton-container--append"
      >
        <div v-for="n in 5" :key="'skeleton-' + n" class="tree-skeleton-item">
          <div class="tree-skeleton-icon"></div>
          <div class="tree-skeleton-content">
            <div class="tree-skeleton-title"></div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch, provide } from 'vue';
import TreeItem from '@components/PositionsTreeNavigator/TreeItem.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  selectedItem: {
    type: Object,
    default: null,
  },
  loadingMore: {
    type: Boolean,
    default: false,
  },
  hasMore: {
    type: Boolean,
    default: false,
  },
  onPositionDroppedOnDepartment: {
    type: Function,
    default: null,
  },
  positionDragSource: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['item-selected', 'position-department-drag-over', 'toggle-expand']);

provide('reportPositionDepartmentDragOver', (payload) =>
  emit('position-department-drag-over', payload),
);

const expandedItems = ref({});

const treeData = computed(() => props.data || []);

function patchExpandedItems(updates) {
  if (!updates || Object.keys(updates).length === 0) return;
  expandedItems.value = { ...expandedItems.value, ...updates };
}

function mergeExpandedItemsFromTree(items, pending = {}) {
  items.forEach((item) => {
    const key = String(item.id);
    if (!(key in expandedItems.value) && !(key in pending)) {
      pending[key] = item.expanded || false;
    }
    if (item.children?.length > 0) {
      mergeExpandedItemsFromTree(item.children, pending);
    }
  });
  return pending;
}

watch(
  () => props.data,
  (items) => {
    const pending = mergeExpandedItemsFromTree(items || []);
    patchExpandedItems(pending);
  },
  { immediate: true },
);

function setExpandedItem(itemId, isExpanded) {
  patchExpandedItems({ [String(itemId)]: isExpanded });
}

function findDepartmentById(id, items = treeData.value) {
  const idStr = String(id);
  for (const item of items) {
    if (String(item.id) === idStr) return item;
    if (item.children?.length > 0) {
      const found = findDepartmentById(id, item.children);
      if (found) return found;
    }
  }
  return null;
}

function findDepartmentParent(departmentId, items = treeData.value, parent = null) {
  const idStr = String(departmentId);
  for (const item of items) {
    if (String(item.id) === idStr) return parent;
    if (item.children?.length > 0) {
      const found = findDepartmentParent(departmentId, item.children, item);
      if (found) return found;
    }
  }
  return null;
}

function getDepartmentAncestorPath(departmentId) {
  const path = [];
  let currentId = String(departmentId);
  while (true) {
    const parent = findDepartmentParent(currentId);
    if (!parent) break;
    path.unshift(String(parent.id));
    currentId = String(parent.id);
  }
  return path;
}

function collectDepartmentSubtreeIds(department, ids = []) {
  if (!department || department.type !== 'department') return ids;
  ids.push(String(department.id));
  (department.children || []).forEach((child) => {
    if (child.type === 'department') collectDepartmentSubtreeIds(child, ids);
  });
  return ids;
}

function collapseDepartmentSubtree(department) {
  const ids = collectDepartmentSubtreeIds(department);
  if (ids.length === 0) return;
  patchExpandedItems(Object.fromEntries(ids.map((id) => [id, false])));
}

function collapseSiblingDepartments(itemId, parentId = null) {
  const idStr = String(itemId);
  if (parentId == null || parentId === '') {
    treeData.value.forEach((department) => {
      if (String(department.id) !== idStr && department.type === 'department') {
        collapseDepartmentSubtree(department);
      }
    });
    return;
  }
  const parent = findDepartmentById(parentId);
  if (!parent?.children?.length) return;
  parent.children.forEach((child) => {
    if (child.type === 'department' && String(child.id) !== idStr) {
      collapseDepartmentSubtree(child);
    }
  });
}

function handleToggle(itemId, itemType, parentId) {
  const key = String(itemId);
  const isCurrentlyExpanded = expandedItems.value[key];
  const willBeExpanded = !isCurrentlyExpanded;
  if (willBeExpanded) collapseSiblingDepartments(itemId, parentId);
  patchExpandedItems({ [key]: willBeExpanded });
  emit('toggle-expand', { itemId, itemType, parentId, isExpanded: willBeExpanded });
}

function handleItemSelected(itemData) {
  emit('item-selected', itemData);
}

function handlePositionDepartmentDragOver(payload) {
  emit('position-department-drag-over', payload);
}

function hasNextSameType(items, currentIndex) {
  if (currentIndex >= items.length - 1) return false;
  const currentType = items[currentIndex].type;
  for (let i = currentIndex + 1; i < items.length; i++) {
    if (items[i].type === currentType) return true;
  }
  return false;
}

function expandNodes(ids) {
  if (!Array.isArray(ids) || ids.length === 0) return;
  patchExpandedItems(Object.fromEntries(ids.map((id) => [String(id), true])));
}

function applyDepartmentSelectionExpand(departmentId, options = {}) {
  const { expandSelf = true } = options;
  const id = String(departmentId);
  const ancestorPath = getDepartmentAncestorPath(id);
  if (ancestorPath.length === 0) {
    collapseSiblingDepartments(id, null);
  } else {
    collapseSiblingDepartments(ancestorPath[0], null);
    ancestorPath.forEach((ancestorId) => setExpandedItem(ancestorId, true));
    const parentId = ancestorPath[ancestorPath.length - 1];
    collapseSiblingDepartments(id, parentId);
  }
  setExpandedItem(id, expandSelf);
}

defineExpose({ expandNodes, applyDepartmentSelectionExpand, setExpandedItem, hasNextSameType });
</script>

<style scoped>
.departments-search-and-navigate {
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-radius: 16px;
  background: #fcfcfd;
  padding: 20px 0;
  position: relative;
  min-width: 150px;
  direction: ltr;
  overflow: hidden;
}

.empty-tree-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  min-height: 200px;
}

.empty-tree-state p {
  color: #6b7280;
  font-size: 14px;
  margin: 0;
  text-align: center;
}

.tree-skeleton-container {
  padding: 8px 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tree-skeleton-item {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
  padding: 4px 0;
}

.tree-skeleton-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 4px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: tree-skeleton-pulse 1.2s ease-in-out infinite;
}

.tree-skeleton-content {
  flex: 1;
  min-width: 0;
}

.tree-skeleton-title {
  height: 16px;
  border-radius: 4px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: tree-skeleton-pulse 1.2s ease-in-out infinite;
  max-width: 180px;
}

@keyframes tree-skeleton-pulse {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
